import getWording from "common/utils/wording";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-extraneous-dependencies
import Chart from "react-apexcharts";
import { useMemo } from "react";
import Icon from "components/Icon/Icon";
import fr from "apexcharts/dist/locales/fr.json";
import { formatDateByScale, formatTooltipByScale } from "common/utils/formatDateByScale";
import { useOrganization } from "common/hooks/useOrganization";
import { Constants } from "constants/Constants";
import { getStoredData } from "common/utils/fnAsyncStorage";

export default function ThreadStatistics({
  data,
  isDetail,
  startDate,
  endDate,
  scale,
}: {
  data: any;
  isDetail: boolean;
  startDate?: Date;
  endDate?: Date;
  scale?: string;
}) {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const wording = getWording();
  const wordingSingular = getWording(false, true);
  const wordingPlural = getWording(false, false);

  const handleDownloadCsv = async () => {
    if (organization?.id) {
      try {
        const token = await getStoredData("token");
        const response = await fetch(`${Constants.API_URL}/threads/statscsv/${organization.id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `statistiques-echanges-${organization.id}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erreur lors du téléchargement:", error);
      }
    }
  };

  const stats = useMemo(
    () => [
      {
        name: t("stats.nbTotalThreads", { wording }),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data && data.nbThreads ? data.nbThreads : 0,
      },
      {
        name: t("stats.nbThreadsAnswered", { wording }),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data && data.nbThreadsAnswered ? data.nbThreadsAnswered : 0,
      },
      {
        name: t("stats.nbThreadsWaitingAnswer", { wording }),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat:
          data && data.nbThreadsWaitingAnswer ? data.nbThreadsWaitingAnswer : 0,
      },
    ],
    [data, t, wording]
  );

  // Préparation des données pour le graphique
  // Utilise directement les buckets de dates retournés par l'API (déjà agrégés selon l'échelle choisie)
  const chartData = useMemo(() => {
    if (!data?.threadsPerSite) return { series: [], categories: [] };

    // Collecter toutes les clés de dates uniques (ordre conservé via Map)
    const dateKeys = new Map<string, number>();
    for (const site of data.threadsPerSite) {
      for (const dataPoint of site.data) {
        if (!dateKeys.has(dataPoint.date)) {
          dateKeys.set(dataPoint.date, 0);
        }
      }
    }

    // Trier les clés chronologiquement
    const sortedDates = Array.from(dateKeys.keys()).sort();

    // Pour chaque date, accumuler les valeurs par site (pour le tooltip)
    const detailsByDate = new Map<string, Map<string, number>>();
    const sumByDate = new Map<string, number>();

    for (const date of sortedDates) {
      detailsByDate.set(date, new Map());
      sumByDate.set(date, 0);
    }

    for (const site of data.threadsPerSite) {
      for (const dataPoint of site.data) {
        if (dataPoint.value > 0) {
          const siteDetails = detailsByDate.get(dataPoint.date);
          if (siteDetails) {
            siteDetails.set(site.site, (siteDetails.get(site.site) || 0) + dataPoint.value);
            sumByDate.set(dataPoint.date, (sumByDate.get(dataPoint.date) || 0) + dataPoint.value);
          }
        }
      }
    }

    // Tooltip details indexed by position (pour accès dans custom tooltip)
    const detailsByIndex = sortedDates.map((d) => {
      const details = detailsByDate.get(d);
      if (!details || details.size === 0) return "Aucun";
      return Array.from(details.entries() as Iterable<[string, number]>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([site, count]) => `${site}: ${count}`)
        .join('<br>');
    });

    // {x: timestamp, y: value} — format requis pour xaxis.type "datetime" sans categories
    const series = [{
      name: t("stats.nbThreads"),
      data: sortedDates.map((d) => ({
        x: new Date(d).getTime(),
        y: sumByDate.get(d) || 0,
      })),
    }];

    return { series, detailsByIndex };
  }, [data, t]);

  const threadsPerType = {
    labels: Object.keys(data?.threadsPerType || {}),
    datasets: [
      {
        label: t("stats.siteTypes"),
        data: Object.values(data?.threadsPerType || {}),
        borderWidth: 1,
      },
    ],
  };

  return (
    <>
      <dl className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        {stats.map((item) => (
          <div
            key={item?.name}
            className="relative bg-white pt-3 px-2 sm:pt-2 sm:px-2 shadow rounded-lg overflow-hidden"
          >
            <dt>
              <div className="absolute bg-indigo-500 rounded-md p-3">
                {item?.icon}
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                {item?.name}
              </p>
            </dt>
            <dd className="ml-16 pb-2 flex items-baseline sm:pb-3">
              <p className="text-2xl font-semibold text-gray-900">
                {item?.stat}
              </p>
            </dd>
          </div>
        ))}
      </dl>

      <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="h-80 justify-center white-box">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t("stats.threadsBySite", { wording })}
            </h3>
            <button
              onClick={handleDownloadCsv}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
            >
              {t("stats.downloadStats")}
            </button>
          </div>
          <Chart
            options={{
              chart: {
                id: "threadsBySite",
                locales: [fr],
                defaultLocale: "fr",
              },
              xaxis: {
                type: "datetime",
                labels: {
                  rotate: -45,
                  datetimeUTC: false,
                  style: { fontSize: '12px' },
                  formatter: (value: string) => formatDateByScale(Number(value), scale),
                },
                ...(startDate && { min: new Date(startDate).getTime() }),
                ...(endDate && { max: new Date(endDate).getTime() }),
              },
              yaxis: {
                title: { text: t("stats.nbThreads") },
              },
              tooltip: {
                enabled: true,
                custom: function ({ series, seriesIndex, dataPointIndex }) {
                  const details = chartData.detailsByIndex?.[dataPointIndex] || "Aucun";
                  const date = formatTooltipByScale(chartData.series[0]?.data[dataPointIndex]?.x, scale);
                  return `
                    <div class="p-2">
                      <div class="font-bold">${date}</div>
                      <div class="mt-1">${t("stats.nbThreads")}: ${series[seriesIndex][dataPointIndex]}</div>
                      <div class="mt-2 font-semibold">${t("titlePages.sites", { wording: wordingSingular })}:</div>
                      <div class="mt-1">${details}</div>
                    </div>
                  `;
                },
              },
              noData: {
                text: "Aucune données à afficher",
                align: "center",
                verticalAlign: "middle",
                offsetX: 0,
                offsetY: 0,
                style: {
                  fontSize: "18px",
                  color: "#263238",
                },
              },
            }}
            series={chartData.series}
            height="100%"
            type="bar"
          />
        </div>
        <div className="h-[500px] justify-center white-box">
          <Chart
            options={{
              chart: {
                id: "threadsByType",
                locales: [fr],
                defaultLocale: "fr",
                toolbar: {
                  show: true,
                  tools: {
                    download: true,
                    selection: false,
                    zoom: false,
                    zoomin: false,
                    zoomout: false,
                    pan: false,
                    reset: false,
                  },
                },
              },
              title: {
                text: t("stats.threadsByType"),
                align: "center",
                margin: 18,
                style: {
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#263238",
                },
              },
              legend: {
                position: "bottom",
              },
              labels: threadsPerType.labels,
              noData: {
                text: "Aucune données à afficher",
                align: "center",
                verticalAlign: "middle",
                offsetX: 0,
                offsetY: 0,
                style: {
                  fontSize: "18px",
                  color: "#263238",
                },
              },
            }}
            series={
              data && data.threadsPerType
                ? (Object.values(data.threadsPerType || {}) as number[])
                : []
            }
            height="100%"
            type="pie"
          />
        </div>
      </div>
    </>
  );
}
